import { createContext, useContext } from "react";

import type { SuggestionOption } from "../types";

// Context state interface
export interface AutocompleteContextState {
	// Core state
	isOpen: boolean;
	highlightedIndex: number;
	filteredSuggestions: SuggestionOption[];

	// Computed state
	groupedSuggestions: Array<{
		label: string;
		options: SuggestionOption[];
	}>;

	// UI state
	position: "above" | "below";
	maxHeight: number;
	isPositioned: boolean; // New: indicates if positioning calculation is complete

	// Loading and error states
	isLoading?: boolean;
	error?: Error | null;
}

// Context actions interface
export interface AutocompleteContextActions {
	// State setters
	setIsOpen: (isOpen: boolean) => void;
	setHighlightedIndex: (index: number) => void;

	// Event handlers
	onSuggestionSelect: (suggestion: SuggestionOption) => void;
	onHighlightChange: (index: number) => void;

	// Navigation
	navigateUp: () => void;
	navigateDown: () => void;
	selectHighlighted: () => void;
	close: () => void;
}

// Combined context value
export interface AutocompleteContextValue {
	state: AutocompleteContextState;
	actions: AutocompleteContextActions;
}

// Create the context
const AutocompleteContext = createContext<AutocompleteContextValue | null>(null);

// Provider component
export interface AutocompleteProviderProps {
	children: React.ReactNode;
	value: AutocompleteContextValue;
}

export const AutocompleteProvider = ({ children, value }: AutocompleteProviderProps) => (
	<AutocompleteContext.Provider value={value}>{children}</AutocompleteContext.Provider>
);

// Hook to use the context
export const useAutocompleteContext = (): AutocompleteContextValue => {
	const context = useContext(AutocompleteContext);

	if (!context) {
		throw new Error("useAutocompleteContext must be used within an AutocompleteProvider");
	}

	return context;
};

// Selector hooks for specific parts of state
export const useAutocompleteState = () => {
	const { state } = useAutocompleteContext();
	return state;
};

export const useAutocompleteActions = () => {
	const { actions } = useAutocompleteContext();
	return actions;
};
